import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Stack,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormLabel,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Tooltip,
  Button,
  FormControlLabel,
  Switch,
} from "@mui/material";
import "../../../components/styles/popupStyle.css";
import { YesOrNo } from "../../../constants/YesOrNo";
import { ArrowDropDownIcon } from "@mui/x-date-pickers/icons";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import {
  validateString,
  validationLength,
  validationType,
  validationMessages,
  MAX_GOLF_PLAYER_TYPE_DAYS_OUT_START,
  MAX_GOLF_PLAYER_TYPE_DAYS_OUT_END,
} from "../../actions/validations";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import { GolfPropertyEnum } from "../../../constants/NavbarList";
import { Guarantee } from "../../../constants/GuaranteeField";
import {
  golfTooltipHeaders,
  originsTooltip,
} from "../../../constants/TooltipInformation";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";

const GolfPlayerTypes = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [playerType, setPlayerType] = useState<string | null>(null);
  const [daysOutStart, setDaysOutStart] = useState<String | null>(null);
  const [daysOutEnd, setDaysOutEnd] = useState<String | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [availableOnWeb, setAvailableOnWeb] = useState<boolean>(false);
  const [noneField, setNoneField] = useState<boolean>(false);
  const [member, setMember] = useState<boolean>(false);
  const [resort, setResort] = useState<boolean>(false);
  const [creditCard, setCreditCard] = useState<boolean>(false);
  const [nameError, setNameError] = useState<boolean>(false);
  const [daysOutStartError, setDaysOutStartError] = useState<boolean>(false);
  const [daysOutEndError, setDaysOutEndError] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [GolfPlayerTypes, setGolfPlayerTypes] = useState<any>([]);
  const [guarantee, setGuarantee] = useState<any>(null);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  const parseDaysOutValue = (value: String | null): number | null => {
    if (value === null || String(value).trim() === "") {
      return null;
    }
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  };

  const validateInput = () => {
    if (!playerType) {
      setAlertMessage("Player type is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setNameError(false);
    setDaysOutStartError(false);
    setDaysOutEndError(false);

    if (
      playerType &&
      !validateString(
        playerType,
        validationLength.playerType,
        validationType.SPACE,
      )
    ) {
      setNameError(true);
      return false;
    }

    const startNum = parseDaysOutValue(daysOutStart);
    if (startNum !== null && startNum > MAX_GOLF_PLAYER_TYPE_DAYS_OUT_START) {
      setDaysOutStartError(true);
      setAlertMessage(validationMessages.daysOutStartMax);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    const endNum = parseDaysOutValue(daysOutEnd);
    if (endNum !== null && endNum > MAX_GOLF_PLAYER_TYPE_DAYS_OUT_END) {
      setDaysOutEndError(true);
      setAlertMessage(validationMessages.daysOutEndMax);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    return true;
  };

  // useEffect(() => {
  //   validateInput();
  // }, [playerType]);

  const updateGolfPlayerTypes = () => {
    if (!validateInput()) {
      return;
    }
    if (
      checkDuplicate(
        (GolfPlayerTypes ?? []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.PLAYERTYPE,
        playerType,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setNameError(false);
    setGolfPlayerTypes((prev: any) => {
      return (prev ?? []).map((item: any) => {
        if (item.id === selectedId) {
          return {
            ...item,
            ...payload,
          };
        }
        return item;
      });
    });
    editGolfPlayerTypes(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${GolfPropertyEnum.playerTypes} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addGolfPlayerTypes = () => {
    if (!validateInput()) {
      return;
    }
    if (
      checkDuplicate(
        GolfPlayerTypes ?? [],
        DNDFieldsEnum.PLAYERTYPE,
        playerType,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setGolfPlayerTypes((prev: any) => {
      return updateData(
        prev != null
          ? [
              ...prev,
              {
                id: (GolfPlayerTypes?.length ?? 0) + 1,
                ...payload,
              },
            ]
          : [
              {
                id: 1,
                ...payload,
              },
            ],
      );
    });
    setLastUpdatedRecord(payload);
    editGolfPlayerTypes(null);
    setEditClicked(false);
  };

  const editGolfPlayerTypes = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setPlayerType(list?.["Player Type"]);
    setDaysOutStart(list?.["Days out Start"]);
    setDaysOutEnd(list?.["Days out End"]);
    setGuarantee(list?.["Guarantee"]);
    setAvailableOnWeb(list?.["Available on Web"]);
    setActive(list?.["Active"]);
    setDaysOutStartError(false);
    setDaysOutEndError(false);
  };

  const deleteGolfPlayerTypes = (id: number) => {
    setGolfPlayerTypes(deleteItem(GolfPlayerTypes ?? [], id));
  };
  useEffect(() => {
    fieldValues && setGolfPlayerTypes(fieldValues);
  }, []);

  useEffect(() => {
    data(GolfPlayerTypes);
    editClicked &&
      setSelectedId(checkData(GolfPlayerTypes, selectedId) ? selectedId : null);
  }, [GolfPlayerTypes]);

  const payload = {
    "Player Type": playerType,
    "Days out Start": daysOutStart,
    "Days out End": daysOutEnd,
    Guarantee: guarantee,
    "Available on Web": availableOnWeb,
    Active: active,
  };
  return (
    <Stack>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack direction="row" width="100%" justifyContent="space-between">
          <Typography variant="h6" component="div" sx={textStyle}>
            Player Types
            <Tooltip
              title={tooltipComponent(golfTooltipHeaders.playerTypeHeader)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px" justifyContent="space-between">
            {editClicked && (
              <ClearButton
                click={() => {
                  editGolfPlayerTypes(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton
                click={() => {
                  editGolfPlayerTypes(lastUpdatedRecord);
                }}
              />
            )}
          </Stack>
        </Stack>
        <Stack>
          <FormLabel component="legend">
            Player Type
            <Tooltip title={tooltipComponent(golfTooltipHeaders.playerType)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <TextField
            id="playerType"
            variant="outlined"
            placeholder="Player Type"
            value={playerType ?? ""}
            required
            helperText={nameError ? validationMessages.playerType : ""}
            error={nameError}
            onChange={(e: any) => {
              setPlayerType(e.target.value);
            }}
          />
        </Stack>
        <Stack>
          <FormLabel component="legend">
            Days Out Start
            <Tooltip title={tooltipComponent(golfTooltipHeaders.daysOutStart)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <TextField
            id="daysOutStart"
            variant="outlined"
            placeholder="Days Out Start"
            value={daysOutStart ?? ""}
            type="number"
            inputProps={{ min: 0, max: MAX_GOLF_PLAYER_TYPE_DAYS_OUT_START }}
            error={daysOutStartError}
            helperText={
              daysOutStartError ? validationMessages.daysOutStartMax : ""
            }
            onChange={(e: any) => {
              setDaysOutStart(e.target.value);
              setDaysOutStartError(false);
            }}
          />
        </Stack>
        <Stack>
          <FormLabel component="legend">
            Days Out End
            <Tooltip title={tooltipComponent(golfTooltipHeaders.daysOutEnd)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <TextField
            id="daysOutEnd"
            variant="outlined"
            placeholder="Days Out End"
            value={daysOutEnd ?? ""}
            type="number"
            inputProps={{ min: 0, max: MAX_GOLF_PLAYER_TYPE_DAYS_OUT_END }}
            error={daysOutEndError}
            helperText={daysOutEndError ? validationMessages.daysOutEndMax : ""}
            onChange={(e: any) => {
              setDaysOutEnd(e.target.value);
              setDaysOutEndError(false);
            }}
          />
        </Stack>
        <Stack>
          <FormLabel component="legend">
            Guarantee
            <Tooltip
              title={tooltipComponent(golfTooltipHeaders.guaranteePlayerType)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>{" "}
          <Select
            id="guarantee"
            value={guarantee ?? ""}
            onChange={(e: any) => setGuarantee(e.target.value)}
            sx={{ width: "100%" }}
          >
            {Guarantee.map((type: any, index: any) => (
              <MenuItem placeholder="Active" value={type} key={index}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </Stack>
        <Stack direction="row" gap="2px">
          <FormControlLabel
            control={
              <Switch checked={active} onChange={() => setActive(!active)} />
            }
            label="Active"
          />
          <Tooltip title={tooltipComponent(originsTooltip.active)}>
            <InfoIcon fontSize="small" />
          </Tooltip>
        </Stack>
        <Stack direction="row" gap="2px">
          <FormControlLabel
            control={
              <Switch
                checked={availableOnWeb}
                onChange={() => setAvailableOnWeb(!availableOnWeb)}
              />
            }
            label="Available on Web?"
          />
          <Tooltip
            title={tooltipComponent(
              golfTooltipHeaders.availableForWebPlayerType,
            )}
          >
            <InfoIcon fontSize="small" />
          </Tooltip>
        </Stack>
        <Stack direction="row" width="100%" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() =>
              selectedId ? updateGolfPlayerTypes() : addGolfPlayerTypes()
            }
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD}
            Player Type
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Player Types Available</Typography>
          <Stack direction="column" gap="10px">
            {GolfPlayerTypes ? (
              <DraggableItemList
                itemData={GolfPlayerTypes}
                onOrderUpdate={(order) => setGolfPlayerTypes(order)}
                onEdit={(info) => editGolfPlayerTypes(info)}
                onDelete={(id) => deleteGolfPlayerTypes(id)}
                code={DNDFieldsEnum.PLAYERTYPE}
                name={DNDFieldsEnum.AVAILABLEONWEB}
              />
            ) : (
              <Typography variant="body2">
                No Player Types Information Available
              </Typography>
            )}
          </Stack>
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};
export default GolfPlayerTypes;
